import { BureauParentBadgeFactory } from '@domains/bureau'
import { FeatureSettingsTabFactory } from '@domains/subscription'
import styled from '@emotion/styled'
import { AmprLogo } from '@figma/svgs/AmprLogo'
import { CostPocketLogo } from '@figma/svgs/CostPocketLogo.tsx'
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { useNotesContext } from 'contexts/Notes'
import { useIsLoadingWithPermission, usePermissions } from 'contexts/permissions'
import dayjs from 'dayjs'
import FeatherIcon from 'feather-icons-react'
import Colors from 'figma/panda/Colors'
import { DoneAiLogo } from 'figma/svgs/DoneAiLogo.tsx'
import EstoniaFlag from 'figma/svgs/EstoniaFlag.tsx'
import FindityLogo from 'figma/svgs/FindityLogo'
import GermanyFlag from 'figma/svgs/GermanyFlag.tsx'
import LatviaFlag from 'figma/svgs/LatviaFlag.tsx'
import NetherlandsFlag from 'figma/svgs/NetherlandsFlag.tsx'
import NoFlag from 'figma/svgs/NOFlag.tsx'
import { NordeaLogo } from 'figma/svgs/nordeaLogo.tsx'
import { SetlyLogo } from 'figma/svgs/SetlyLogo.tsx'
import { VismaEconomicLogo } from 'figma/svgs/VismaEconomicLogo.tsx'
import { VismaPayLogo } from 'figma/svgs/VismaPayLogo.tsx'
import { TabSets } from 'helpers/TabsHelpers'
import { BillingSettingsV3 } from 'pages/CustomerCardTab/BillingSettings/BillingSettingsV3.tsx'
import { CustomerPaymentWhitelist } from 'pages/CustomerPaymentWhitelist/CustomerPaymentWhitelist'
import { useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { colors } from 'themes'
import { useActivationGuide, useActivationGuideVisibility, useCardCredits, useCustomer, usePatchCustomer } from '../api/react-query'
import { CardCreditDataState, CardPartner, CustomerFeature, CustomerProduct, KycAccessLevel } from '../api/swagger/definitions/backoffice'
import DenmarkFlag from '../figma/svgs/DenmarkFlag.tsx'
import EtaskuLogo from '../figma/svgs/EtaskuLogo'
import Finland24 from '../figma/svgs/Finland24'
import FortnoxLogo from '../figma/svgs/FortnoxLogo'
import Kleer from '../figma/svgs/kleer.svg?react'
import Sweden24 from '../figma/svgs/Sweden24'
import WintLogo from '../figma/svgs/WintLogo'
import Spacings from '../figma/tokens/Spacings'
import { Countries } from '../flamingo/config/ConfigCountries'
import type { AccountStatuses } from '../helpers/AccountStatusHelper'
import { prettifyEnumLabel } from '../helpers/CreditOnCardHelpers'
import useMaterialNotification from '../hooks/useMaterialNotification'
import TextKeys from '../libs/TextKeys'
import FigmaBox from '../mynt-components/components/FigmaBox'
import { LightTooltip } from '../mynt-components/components/MyntTooltip'
import SVGContainer from '../mynt-components/components/SVGContainer'
import Tabs from '../mynt-components/components/Tabs'
import { BO_MAX_CONTENT_WIDTH } from '../mynt-components/WeakHardCodedSizes'
import CustomerCardTab from '../pages/CustomerCardTab/CustomerCardTab.tsx'
import AccountStatus from './AccountStatus'
import { CardPartnerBadge } from './CardPartnerBadge'
import { CustomerBureauPanel } from './CustomerBureauPanel'
import CustomerGeneralForm from './CustomerFormGeneralContainer'
import { CustomerNotes, useCustomerNotesDataProvider } from './CustomerNotes'
import { CustomerPeople } from './CustomerPeople'
import CustomerScoringTabs from './CustomerScoringTabs'
import DirectDebitTab from './DirectDebitTab'
import TextContainer from './TextContainer'

const copy = async (text: string) => window.navigator.clipboard.writeText(text)

const CountryToIcon = {
  [Countries.SE]: Sweden24,
  [Countries.FI]: Finland24,
  [Countries.DK]: DenmarkFlag,
  [Countries.NO]: NoFlag,
  [Countries.NL]: NetherlandsFlag,
  [Countries.EE]: EstoniaFlag,
  [Countries.LV]: LatviaFlag,
  [Countries.DE]: GermanyFlag,
}

const PartnerStyles = (partner: CardPartner) => {
  if (partner === CardPartner.NONE) return null

  switch (partner) {
    case CardPartner.PE: {
      return {
        logo: <Kleer height={22} width="auto" />,
        color: '#240238',
        fill: '#FFAB00',
      }
    }

    case CardPartner.WINT: {
      return {
        logo: <WintLogo height={22} width="auto" />,
        color: '#0bc2f9',
        fill: 'white',
      }
    }

    case CardPartner.FORTNOX: {
      return {
        logo: <FortnoxLogo height={16} width="auto" />,
        color: 'rgb(0, 37, 23)',
        fill: 'white',
      }
    }

    case CardPartner.ETASKU: {
      return {
        logo: <EtaskuLogo height={18} width="auto" />,
        color: '#F9F9F9',
      }
    }

    case CardPartner.FINDITY: {
      return {
        logo: <FindityLogo height={18} width="auto" />,
        color: '#012a2d',
      }
    }

    case CardPartner.VISMAECONOMIC: {
      return {
        logo: <VismaEconomicLogo height={24} width="auto" />,
        color: 'white',
      }
    }
    case CardPartner.VISMAPAY: {
      return {
        logo: <VismaPayLogo height={24} width="auto" />,
        color: 'white',
      }
    }

    case CardPartner.SETLY: {
      return {
        logo: <SetlyLogo height={24} />,
        color: 'white',
      }
    }

    case CardPartner.NORDEA: {
      return {
        logo: <NordeaLogo height={22} width="auto" />,
        color: '#0000a0',
      }
    }

    case CardPartner.DEHAAN: {
      return {
        logo: (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', paddingTop: 4 }}>
            {/** biome-ignore lint/a11y/useAltText: <explanation> */}
            <img
              width="auto"
              height={18}
              src="data:image/webp;base64,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"
            />
          </div>
        ),
        color: '#0236e3',
      }
    }

    case CardPartner.AMPR: {
      return {
        logo: <AmprLogo />,
        color: 'white',
      }
    }

    case CardPartner.COSTPOCKET: {
      return {
        logo: <CostPocketLogo height={22} width="auto" />,
        color: 'white',
      }
    }

    case CardPartner.DONE_AI: {
      return {
        logo: <DoneAiLogo height={22} width="auto" color="white" />,
        color: '#0b0425',
      }
    }

    default: {
      return {
        logo: (
          <span>
            missing partner logo {partner},{' '}
            <a target="_BLANK" href="slack://channel?team=TBEGNHMN0&id=C05QKJQ2G4F" rel="noreferrer">
              let us know
            </a>
          </span>
        ),
        color: '',
      }
    }
  }
}

const PartnerDisconnectedBadge = styled.div<{ color: string; fill?: string; stroke?: string }>`
    padding: ${Spacings.minimum} ${Spacings.tiny};
    border-radius: 8px;
    background-color: ${(props) => props.color};
    color: #1c1b1f;
    cursor: default;
    font-size: 14px;

    path {
        fill: ${(props) => props.fill};
        stroke: ${(props) => props.stroke};
    }
`

const PartnerDisconnected = ({ visible }) => {
  if (!visible) return null

  return (
    <PartnerDisconnectedBadge color={colors.product.error[600]}>
      <Tooltip title="This customer has been removed by the card partner">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'center' }}>
          <RemoveCircleIcon style={{ fontSize: 18 }} />
          <span>Disconnected by partner</span>
        </Box>
      </Tooltip>
    </PartnerDisconnectedBadge>
  )
}

const CustomerDetailsContainer = () => {
  const permissions = {
    activationGuide: usePermissions('activation-guide.status'),
    activationGuideVisibility: usePermissions('activation-guide.visibility'),
    cardCredits: usePermissions('customers.cards.credits.view'),
    people: usePermissions('customers.people.view'),
    scoring: usePermissions('customers.scoring'),
    directDebit: usePermissions('customers.direct-debit'),
    card: usePermissions('customers.card'),
    cardTransactions: usePermissions('customers.cards.transactions.view'),
    credits: usePermissions('customers.credits'),
    rollingCredit: usePermissions('rolling-credit'),
    bureau: usePermissions('customers.bureau'),
    whitelist: usePermissions('customers.payment.whitelist'),
    billingSettings: usePermissions('customers.billing-settings'),
    featureSettings: usePermissions('customers.feature-settings'),
  }

  const { customerId } = useParams<'customerId'>() as Record<string, string>
  const { tab1: activeTab = null } = useParams<'tab1'>() as Record<string, string>

  const navigate = useNavigate()
  const mutation = usePatchCustomer(customerId)
  const { data: activationGuide } = useActivationGuide(customerId, { enabled: permissions.activationGuide })
  const { data: cardCredits } = useCardCredits(customerId, { enabled: permissions.cardCredits })

  const customerNotesProps = useCustomerNotesDataProvider(customerId, { enabled: true })
  const [notesContext] = useNotesContext()

  const { isLoading: customerIsLoading, data: customer } = useCustomer(customerId, {
    retry: false,
    refetchOnWindowFocus: false,
  })

  const { data: activationGuideVisibility, isLoading: _activationGuideVisibilityIsLoading } = useActivationGuideVisibility(customerId, {
    enabled: permissions.activationGuideVisibility,
  })

  const activationGuideVisibilityIsLoading = useIsLoadingWithPermission(_activationGuideVisibilityIsLoading, 'activation-guide.visibility')

  const notification = useMaterialNotification()

  const features = useMemo(() => Array.from(customer?.features || []), [customer])
  const products = useMemo(() => Array.from(customer?.products || []), [customer])

  const isLoading = [customerIsLoading, activationGuideVisibilityIsLoading].some(Boolean)

  // If we have issues with customer not loading, because of subscriptionPlan, move this check to the subscriptionPlan dropdown.
  if (isLoading || !customer) {
    return <CircularProgress sx={{ display: 'block', margin: '50px auto' }} />
  }

  const hasCardProduct = products.includes(CustomerProduct.CARD)
  const hasCardCredit = products.includes(CustomerProduct.CARD_CREDIT)
  const hasCardTopup = features.includes(CustomerFeature.TOPUP)
  const hasOrderCard = features.includes(CustomerFeature.CARD_ORDER)

  const hasApprovedCardCredit = !!cardCredits && cardCredits.some((cardCredit) => cardCredit?.state === CardCreditDataState.APPROVED)

  const statuses: AccountStatuses = {
    products: [hasCardProduct ? KycAccessLevel.GREEN : KycAccessLevel.GREY],
    kyc: [activationGuide?.statuses.kyc || KycAccessLevel.GREY, customer.kycAccessLevel] as KycAccessLevel[],
    cardFeatures: [
      hasOrderCard ? KycAccessLevel.GREEN : KycAccessLevel.GREY,
      hasCardTopup ? KycAccessLevel.GREEN : KycAccessLevel.GREY,
      hasCardCredit ? KycAccessLevel.GREEN : KycAccessLevel.GREY,
    ],
    cardActivationStatus: [
      activationGuide?.statuses.addMembersAndOrderCards || KycAccessLevel.GREY,
      activationGuide?.statuses.connectAcountingSystem || KycAccessLevel.GREY,
      hasApprovedCardCredit ? KycAccessLevel.GREEN : KycAccessLevel.GREY,
      activationGuide?.statuses.addMoney || KycAccessLevel.GREY,
      activationGuide?.statuses.madePurchase || KycAccessLevel.GREY,
    ] as KycAccessLevel[],
  }

  const copyCompanyName = () => {
    const companyName = customer.companyName || '-'
    notification(`Copied name '${companyName}'`, 'info')
    copy(companyName)
  }

  const copyOrgNo = () => {
    const orgNo = customer.orgNo || '-'
    notification(`Copied orgNo '${orgNo}'`, 'info')
    copy(orgNo)
  }

  const copyCustomerId = () => {
    const id = customer.id || '-'
    notification(`Copied customer id '${id}'`, 'info')
    copy(id)
  }

  const flag = CountryToIcon[customer.country]
  const partnerStyle = PartnerStyles(customer.cardPartner)

  const handleTabChange = (tabIndex: number) => {
    const tab = tabs[tabIndex]

    if (!tab) return
    if (tab.onClick) return

    navigate(`${tab.id}`, { replace: true })
  }

  const tabs = [
    {
      id: 'general',
      labelElement: <TextContainer textKey={TextKeys.tabNotSelected} text={'General'} />,
      contentElement: (
        <CustomerGeneralForm
          activationGuideVisibility={activationGuideVisibility}
          customer={customer}
          customerMutation={(partialCustomer) => mutation.mutateAsync(partialCustomer)}
        />
      ),
      enable: true,
    },
    {
      id: 'people',
      labelElement: <TextContainer textKey={TextKeys.tabNotSelected} text={'People'} />,
      contentElement: (
        <Box sx={{ maxWidth: notesContext.open ? '75vw' : '93vw' }}>
          <CustomerPeople customerId={customer.id} />
        </Box>
      ),
      enable: permissions.people,
    },
    {
      id: 'scoring',
      labelElement: <TextContainer textKey={TextKeys.tabNotSelected} text={'Scoring files'} />,
      contentElement: <CustomerScoringTabs customerId={customer.id} />,
      enable: permissions.scoring,
    },
    {
      id: 'direct-debit',
      labelElement: <TextContainer textKey={TextKeys.tabNotSelected} text={'Direct debit'} />,
      contentElement: <DirectDebitTab customerId={customer.id} />,
      enable: permissions.directDebit,
    },
    {
      id: 'card',
      labelElement: <TextContainer textKey={TextKeys.tabNotSelected} text={'Card'} />,
      contentElement: (
        <CustomerCardTab customerId={customer.id} customer={customer} patchCustomerDetails={async (data) => mutation.mutateAsync(data)} />
      ),
      enable: permissions.card || permissions.cardTransactions,
    },
    {
      id: 'rolling-credit',
      labelElement: <TextContainer textKey={TextKeys.tabNotSelected} text={'Rolling credit'} />,
      contentElement: <></>,
      enable: permissions.rollingCredit,
      onClick: () => navigate(`/rolling-credit?customerId=${customerId}`),
    },
    {
      id: 'bureau',
      labelElement: <TextContainer textKey={TextKeys.tabNotSelected} text={'Bureau'} />,
      contentElement: <CustomerBureauPanel customerId={customer.id} />,
      enable: permissions.bureau,
    },
    {
      id: 'payment-whitelist',
      labelElement: <TextContainer textKey={TextKeys.tabNotSelected} text={'Payment Whitelist'} />,
      contentElement: <CustomerPaymentWhitelist customerId={customer.id} />,
      enable: permissions.whitelist,
    },
    {
      id: 'billing-settings',
      labelElement: <TextContainer textKey={TextKeys.tabNotSelected} text={'Billing settings'} />,
      contentElement: <BillingSettingsV3 currency={customer.currency} customerId={customer.id} />,
      enable: permissions.billingSettings,
    },
    {
      id: 'feature-settings',
      labelElement: <TextContainer textKey={TextKeys.tabNotSelected} text={'Feature settings'} />,
      contentElement: <FeatureSettingsTabFactory customerId={customerId} />,
      enable: permissions.featureSettings,
    },
  ]

  const activeTabIndex = tabs.findIndex((t) => t.id === activeTab)

  return (
    <div style={{ display: 'flex', justifyContent: 'center' }}>
      <div style={{ flex: 1, minWidth: 0, maxWidth: `${BO_MAX_CONTENT_WIDTH}px`, padding: '0 32px' }}>
        <CustomerActivationTime isActivated={customer.isActivated} activationDate={customer.activateTransactionsAt} />
        <FigmaBox align="baseline" fullWidth direction="row" justify="space-between">
          <FigmaBox fullWidth>
            <FigmaBox fullWidth direction="row" gap={Spacings.tiny} align="center">
              <FigmaBox
                direction="row"
                gap={Spacings.tiny}
                spacing={Spacings.minimum}
                bottom
                style={{ cursor: 'pointer' }}
                onClick={copyCompanyName}
                align="center"
              >
                <TextContainer textKey={TextKeys.companyNameHeading} text={customer.companyName} />
              </FigmaBox>
              {flag && <SVGContainer SVG={flag} />}
              {partnerStyle && (
                <LightTooltip
                  title={`${prettifyEnumLabel(customer.cardPartner)} card partner`}
                  content={<CardPartnerBadge logo={partnerStyle.logo} svgStyles={{ fill: partnerStyle.fill }} color={partnerStyle.color} />}
                />
              )}
              <PartnerDisconnected visible={customer.partnerDisconnected} />
              <BureauParentBadgeFactory customerId={customer.id} />
            </FigmaBox>
            <FigmaBox bottom={Spacings.tiny} style={{ cursor: 'pointer' }} onClick={copyCustomerId}>
              <TextContainer textKey={TextKeys.companyOrgNum} text={customer.id || '-'} />
            </FigmaBox>
            <FigmaBox style={{ cursor: 'pointer' }} onClick={copyOrgNo}>
              <TextContainer textKey={TextKeys.companyOrgNum} text={customer.orgNo || '-'} />
            </FigmaBox>
          </FigmaBox>

          <AccountStatus customer={customer} statuses={statuses} />
        </FigmaBox>

        <Tabs
          fullPage
          alwaysShowNavHeadings
          tabKeyNameSpace={TabSets.CUSTOMER}
          onTabChange={handleTabChange}
          tabs={tabs}
          tab={activeTabIndex >= 0 ? activeTabIndex : null}
        />
      </div>
      <CustomerNotes {...customerNotesProps} />
    </div>
  )
}

const CustomerActivationTime = ({ isActivated, activationDate }) => {
  if (isActivated) return null

  return (
    <Tooltip
      title={
        <Typography
          variant="subtitle1"
          sx={{
            textAlign: 'center',
          }}
        >
          The customer account can be activated up to one hour later than the time specified
        </Typography>
      }
      placement="top"
    >
      <Box
        sx={{
          padding: '4px 8px',
          display: 'flex',
          alignItems: 'center',
          color: 'black',
          borderRadius: '8px',
          gap: '4px',
          backgroundColor: Colors.alert300,
          width: 'fit-content',
          marginBottom: '16px',
        }}
      >
        <FeatherIcon icon="alert-triangle" size={16} />
        Account activation scheduled at {dayjs(activationDate).utc(true).tz('Europe/Stockholm').format('YYYY-MM-DD HH:mm')}*
      </Box>
    </Tooltip>
  )
}

export default CustomerDetailsContainer
